<script setup>
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useMediaQuery } from '@vueuse/core'
import { useSideBarStore } from '~/stores/sideBar'
import { useAuthStore } from '~/stores/auth'
import { useMessageStore } from '~/stores/messages'

// Auth store
const authStore = useAuthStore()
const { isLoggedIn } = storeToRefs(authStore)

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Fetch sidebar store
const sideBarStore = useSideBarStore()
const { sideBarContent } = storeToRefs(sideBarStore)

// Define initial state for navigation
const isOpen = ref(false)

// Fetch a center list ito check if center route is valid
const { data: centers, suspense: centerSuspense } = useFetchCenterList()

onServerPrefetch(async () => {
    await centerSuspense()
})

// Do some checks
const isCenterPage = computed(() => centers.value?.some(item => item.uid === centerRoute.value))
const hasParking = computed(() => centers.value?.some(item => isCenterPage.value && item.parkingFeatureIsActive))

const closeCenterNavigation = () => {
    isOpen.value = false
}

const handleNavigationClick = async () => {
    if (!isOpen.value) {
        isOpen.value = true

        await navigateWithSidebarQuery({ route })
    } else {
        closeCenterNavigation()
    }
}

const handleAuthClick = async () => {
    closeCenterNavigation()

    const path = isCenterPage.value ? null : '/'

    await navigateWithSidebarQuery({
        path,
        query: {
            profile: null
        },
        route
    })
}

// Close center navigation automatically if side is open  or desktop mode
const isDesktop = useMediaQuery('(min-width: 1024px)')

watch([sideBarContent, isDesktop], () => {
    if (sideBarContent.value.open || isDesktop.value) {
        closeCenterNavigation()
    }
})

const pageNavigationItems = computed(() => {
    return [
        {
            label: staticKeys.value?.header_navigation_home_web,
            itemClass: 'home-link',
            routeName: 'centerUid',
            render: isCenterPage.value,
            requiresCenterPage: false,
            fn: () => closeCenterNavigation()
        },
        {
            label: staticKeys.value?.header_navigation_goodies_web,
            routeName: 'centerUid-coupons',
            render: isCenterPage.value,
            fn: () => closeCenterNavigation()
        },
        {
            label: staticKeys.value?.header_navigation_parking_web,
            routeName: 'centerUid-parking',
            render: hasParking.value,
            fn: () => closeCenterNavigation()
        },
        {
            label: staticKeys.value?.header_navigation_hub_web,
            routeName: 'centerUid-shops',
            render: isCenterPage.value,
            fn: () => closeCenterNavigation()
        },
        {
            label: isLoggedIn.value ? staticKeys.value?.account_label : staticKeys.value?.header_navigation_login_web,
            type: 'auth-btn',
            render: true,
            fn: () => handleAuthClick()
        },
        {
            label: staticKeys.value?.centerselector_select,
            type: 'center-selector',
            itemClass: 'center-selector-link',
            render: isOpen.value,
            fn: () => closeCenterNavigation()
        }
    ]
})
</script>

<template>
    <nav class="page-mainnavigation" :class="{ 'page-mainnavigation-open': isOpen }">
        <ul class="page-mainnavigation-list">
            <PageNavigationItem v-for="(pageNavigationItem, index) in pageNavigationItems" :key="`page-navigation-item-${index}`" :page-navigation-item="pageNavigationItem" />
        </ul>
    </nav>

    <CenterSelector v-if="!isOpen" :center-selector-config="{ position: 'header' }" />

    <div class="page-mainnavigation-burger" @click="handleNavigationClick">
        <span v-for="index in 3" :key="`line-${index}`" class="line" />
    </div>
</template>

<style lang="scss">
.page-mainnavigation {
    @include bp(large1) {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 64px;
        color: $page-navigation;

        @include content-grid-item(9, 3);
    }
}

.page-mainnavigation-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    padding: 0;
    list-style-type: none;
    position: fixed;
    z-index: 2;
    inset: 0;
    background-color: $page-navigation-bg;
    backdrop-filter: blur(7px);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease-out;
    will-change: opacity;
    min-height: 100vh;
    -webkit-overflow-scrolling: auto;
    overflow: auto;

    @include bp(large1) {
        display: contents;
        pointer-events: all;
        min-height: auto;
    }

    .page-mainnavigation-open & {
        display: flex;
        opacity: 1;
        pointer-events: all;
    }
}

.page-mainnavigation-burger {
    grid-row: 1;
    grid-column: 5 / span 1;
    display: flex;
    flex-flow: row wrap;
    justify-self: flex-end;
    justify-content: flex-end;
    width: px2rem(24);
    position: relative;
    z-index: 3;
    cursor: pointer;
    transition: all 0.3s ease-in-out;

    @include bp(medium1) {
        grid-column: 8 / span 1;
        margin-inline-end: 40px;
    }

    @include bp(large1) {
        display: none;
    }

    .line {
        height: 2px;
        background-color: $page-navigation-burger-dark;
        display: block;
        margin-block: 2.5px;
        transition: all 0.3s ease-in-out;

        @include bp(medium1) {
            background-color: $page-navigation-burger-light;
        }

        &:nth-child(1) {
            width: px2rem(18);
        }

        &:nth-child(2) {
            width: px2rem(24);
        }

        &:nth-child(3) {
            width: px2rem(10);
        }
    }
}

.page-mainnavigation-open {
    ~ .page-mainnavigation-burger {
        animation: smallbig 0.6s forwards;

        .line {
            background-color: $page-navigation-close;

            &:nth-child(1),
            &:nth-child(2),
            &:nth-child(3) {
                transition-delay: 0.2s;
            }

            &:nth-child(2) {
                opacity: 0;
            }

            &:nth-child(1) {
                transform: translateY(6px) rotate(45deg);
                width: px2rem(18);
            }

            &:nth-child(3) {
                transform: translateY(-8px) rotate(-45deg);
                width: px2rem(18);
            }
        }
    }
}

@keyframes smallbig {
    0%, 100% {
        transform: scale(1);
    }

    50% {
        transform: scale(0);
    }
}
</style>
